import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Pagination,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/buyer/AppButton";
import AppLoader from "../../components/seller/AppLoader";
import { capitalizeFirst } from "../../utils/stringUtils";
import { fetchPendingTransactions } from "../../features/buyer/buyerSlice";
import { timeAgo } from "../../utils/timeAgo";
import { formatCurrency, formatPrice } from "../../utils/price";

// 👉 Replace later with real API
// import { fetchMyTransactions } from "../../features/buyer/transactionSlice";

const PAGE_SIZE = 10;

const MyTransactions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 👉 TEMP: replace with redux slice later
  const { list: transactions = [], loading } = useSelector(
    (state) => state.buyer.pendingTransactions || {}
  );

  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(fetchPendingTransactions());
  }, [dispatch]);

  const paginatedData = useMemo(() => {
    return transactions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [transactions, page]);

  const totalPages = Math.ceil(transactions.length / PAGE_SIZE);

  const fields = [
    "Sr.No",

    "Product Image",
    "Product Name",
    "Buyer Name",
    "Seller Name",
    "Quantity",
    "Price (AUD)",
    "Discount %",
    "Discounted Price (AUD)",
    "Date",
    "Status",
    "Action",
  ];

  const handlePayNow = (transactionId) => {
    navigate(`/shipping-mode/${transactionId}`);
  };

  const handleViewDetails = (transactionId) => {
  console.log('transaction id:',transactionId)

    navigate(`/transaction-details/${transactionId}`);
  };

  console.log('transaction list:',transactions)

  const tableActionButtonStyle = {
    padding: "4px 10px", // 👈 low vertical padding
    fontSize: "0.75rem",
    minHeight: "28px",
  };

  if (loading) return <AppLoader />;

  return (
    <Card
      sx={{
        p: { xs: 3, sm: 5 },
        mb: 8,
        borderRadius: 3,
        background: "#f9fbff",
        boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <Typography variant="h5" fontWeight={600} mb={3}>
        My Transactions
      </Typography>

      <Box sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1600 }}>
          <TableHead>
            <TableRow>
              {fields.map((h) => (
                <TableCell key={h} align="center" sx={{ fontWeight: 600 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={fields.length}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  No transactions found
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((tx, index) => (
                <TableRow key={tx.id}>
                  {/* Sr.No */}
                  <TableCell align="center">
                    {(page - 1) * PAGE_SIZE + index + 1}
                  </TableCell>

                  {/* Product Image */}
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    <Avatar
                      src={tx.buyer_product.image_url}
                      variant="rounded"
                      sx={{ width: 56, height: 56, mx: "auto" }}
                    />
                  </TableCell>

                  {/* Product Name */}
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    {tx.buyer_product?.name}
                  </TableCell>

                  {/* Buyer */}
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    {tx.buyer?.name || "-"}
                  </TableCell>

                  {/* Seller */}
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    {tx.seller?.name || "-"}
                  </TableCell>

                  {/* Quantity & Unit */}
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    {tx.buyer_product?.quantity}{" "}
                    {tx.buyer_product?.unit_of_measurement}
                  </TableCell>

                  {/* Max Buying Price */}
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    {/* A$ {formatPrice(tx.buyer_product?.max_buying_price) || "-"} */}
                    {formatCurrency(tx.buyer_product?.max_buying_price)}
                  </TableCell>

                  {/* Discount */}
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    {tx.buyer_product?.discount_percentage || "-"}%
                  </TableCell>

                  {/* Discounted Price */}
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    {/* A$ {formatPrice(tx.buyer_product?.discounted_price) || "-"} */}
                    {formatCurrency(tx.buyer_product?.discounted_price)}
                  </TableCell>

                  {/* Date */}
                  <TableCell align="center" sx={{ minWidth: 200 }}>
                    {timeAgo(tx.created_at) || "-"}
                  </TableCell>

                  {/* Status */}
                  <TableCell align="center">
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontWeight: 600,
                        background: "#e6f0ff",
                        color: "#0b4a8f",
                        display: "inline-block",
                      }}
                    >
                      {capitalizeFirst(tx.status) || "-"}
                    </Box>
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center" sx={{ minWidth: 250 }}>
                    <Box
                      sx={{ display: "flex", gap: 1, justifyContent: "center" }}
                    >
                      {tx.payment_status !== "paid" && (
                        <AppButton
                          variant="contained"
                          fullWidth={false}
                          style={tableActionButtonStyle}
                          onClick={() => handlePayNow(tx.id)}
                        >
                          Pay Now
                        </AppButton>
                      )}
                      <AppButton
                        variant="outlined"
                        fullWidth={false}
                        style={tableActionButtonStyle}
                        onClick={() => handleViewDetails(tx.id)}
                      >
                        View Details
                      </AppButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
        </Box>
      )}
    </Card>
  );
};

export default MyTransactions;
